import { getApiBaseUrl } from "../config/env";
import { isAdminAuthLive } from "./adminAuth";

function apiPath(path) {
  const base = getApiBaseUrl();
  if (!base) return path;
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * @returns {Promise<{ total: number; guest: number; invited: number; attendee: number; generatedAt: string | null }>}
 */
export async function fetchAdminPersonSummary() {
  if (!isAdminAuthLive()) {
    await new Promise((r) => setTimeout(r, 200));
    return { total: 0, guest: 0, invited: 0, attendee: 0, generatedAt: null };
  }

  const res = await fetch(apiPath("/api/admin/persons/summary"), {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    let message = "Could not load summary.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const data = await res.json();
  return {
    total: Number(data.total) || 0,
    guest: Number(data.guest) || 0,
    invited: Number(data.invited) || 0,
    attendee: Number(data.attendee) || 0,
    generatedAt: typeof data.generatedAt === "string" ? data.generatedAt : null,
  };
}

/**
 * @returns {Promise<{ persons: Array<Record<string, unknown>> }>}
 */
export async function fetchAdminPersons() {
  if (!isAdminAuthLive()) {
    await new Promise((r) => setTimeout(r, 200));
    return { persons: [] };
  }

  const res = await fetch(apiPath("/api/admin/persons"), {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) {
    let message = "Could not load people.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const data = await res.json();
  return { persons: Array.isArray(data.persons) ? data.persons : [] };
}

/**
 * @param {string} personId
 * @returns {Promise<{ person: Record<string, unknown>; invitationEmail?: { sent?: boolean; reason?: string; error?: string } }>}
 */
export async function inviteAdminPerson(personId) {
  if (!isAdminAuthLive()) {
    throw new Error("Guest management requires the live admin API.");
  }

  const res = await fetch(apiPath(`/api/admin/persons/${encodeURIComponent(personId)}/invite`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    let message = "Could not update this person.";
    try {
      const data = await res.json();
      if (typeof data.message === "string") message = data.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return res.json();
}
